import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { DimensionObject, IPage } from '@common/types';

export type StatePage = {
  pages: { [key: string]: IPage };
  isHaveDevelopPlugin: boolean;
  isDevelopPluginLoading: boolean;
  dimension: DimensionObject;
  loadingDimension: boolean;
  googleMaploaded: boolean;
  valueBindingScreen: Record<string, any>;
  loadingPage: boolean;
  keysBinding: { [key: string]: string[] };
  qrCodeScanner: {
    visible: boolean;
    qrCode: string | null;
    status: 'nothing' | 'cancel' | 'success';
  };
  bindingLoading: boolean;
  pullToRefresh: boolean;
  /**
   * State of the in-app browser presented over the app. See
   * utils/externalBrowser.ts for why screen layers need to know about it.
   */
  externalBrowser: {
    isOpen: boolean;
    isAwaitingKeyboardResync: boolean;
  };
};

const initialState: StatePage = {
  pages: {},
  isHaveDevelopPlugin: false,
  isDevelopPluginLoading: false,
  dimension: { fontScale: 0, height: 0, scale: 0, width: 0 },
  loadingDimension: false,
  externalBrowser: { isOpen: false, isAwaitingKeyboardResync: false },
  googleMaploaded: false,
  valueBindingScreen: {},
  loadingPage: false,
  keysBinding: {},
  qrCodeScanner: {
    visible: false,
    qrCode: null,
    status: 'nothing',
  },
  bindingLoading: true,
  pullToRefresh: false,
};

const pageSlice = createSlice({
  name: 'pageInfo',
  initialState: initialState,
  reducers: {
    loadPages: (
      state: StatePage,
      action: PayloadAction<{ [key: string]: IPage }>
    ) => {
      if (action.payload) {
        state.pages = action.payload;
      }
    },
    loadDevelopPlugin: (
      state: StatePage,
      action: PayloadAction<{ isDevelopPluginLoading: boolean }>
    ) => {
      state.isDevelopPluginLoading = action.payload.isDevelopPluginLoading;
    },
    setHaveDevelopPlugin: (
      state: StatePage,
      action: PayloadAction<{ isHaveDevelopPlugin: boolean }>
    ) => {
      state.isHaveDevelopPlugin = action.payload.isHaveDevelopPlugin;
    },
    setDimensionStore: (
      state: StatePage,
      action: PayloadAction<DimensionObject>
    ) => {
      if (action.payload) {
        state.dimension = action.payload;
        state.loadingDimension = false;
      }
    },
    setLoadingDimension: (state: StatePage, action: PayloadAction<boolean>) => {
      state.loadingDimension = action.payload;
    },
    setExternalBrowserState: (
      state: StatePage,
      action: PayloadAction<StatePage['externalBrowser']>
    ) => {
      state.externalBrowser = action.payload;
    },
    setGoogleMaploaded: (state: StatePage, action: PayloadAction<boolean>) => {
      state.googleMaploaded = action.payload;
    },
    setValueBindingScreen: (state: StatePage, action: PayloadAction<any>) => {
      state.valueBindingScreen = {
        ...state.valueBindingScreen,
        ...action.payload.data,
      };
      state.keysBinding[action.payload.screenUuid] = Object.keys(
        action.payload.data
      );
    },
    removeValueBindingScreen: (
      state: StatePage,
      action: PayloadAction<any>
    ) => {
      const arrId = state.keysBinding[action.payload] || [];
      if (arrId.length <= 0) return;
      for (let i = 0; i < arrId.length; i++) {
        delete state.valueBindingScreen[arrId[i]];
      }
    },
    setLoadingPage: (state: StatePage, action: PayloadAction<any>) => {
      state.loadingPage = action.payload;
    },
    showQrCodeScanner: (state: StatePage, action: PayloadAction<boolean>) => {
      state.qrCodeScanner.visible = action.payload;
    },
    onQrCodeSuccess: (state: StatePage, action: PayloadAction<string>) => {
      state.qrCodeScanner = {
        visible: false,
        qrCode: action.payload,
        status: 'success',
      };
    },
    onQrCodeCancel: (state: StatePage) => {
      state.qrCodeScanner = {
        visible: false,
        qrCode: null,
        status: 'cancel',
      };
    },
    resetQrCodeScanner: (state: StatePage) => {
      state.qrCodeScanner = {
        ...state.qrCodeScanner,
        visible: false,
        status: 'nothing',
      };
    },
    setBindingLoading: (state: StatePage, action: PayloadAction<boolean>) => {
      state.bindingLoading = action.payload;
    },
    setPullToRefresh: (state: StatePage, action: PayloadAction<boolean>) => {
      state.pullToRefresh = action.payload;
    },
  },
});

export const {
  loadPages,
  setDimensionStore,
  setLoadingDimension,
  setExternalBrowserState,
  loadDevelopPlugin,
  setHaveDevelopPlugin,
  setGoogleMaploaded,
  setValueBindingScreen,
  setLoadingPage,
  removeValueBindingScreen,
  showQrCodeScanner,
  onQrCodeSuccess,
  resetQrCodeScanner,
  onQrCodeCancel,
  setBindingLoading,
  setPullToRefresh,
} = pageSlice.actions;
export default pageSlice.reducer;
